import { Result } from "../../api/types";
import { fetchData, writeFormData } from "../../clients/api";

/** `yyyy/MM/dd HH:mm:ss` in local time — used in add/update line-item `import_batch` strings for one submit. */

export function formatLineItemImportBatchTimestamp(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

type QtoReviewProductInfo = { name: string; uom: string };

export function canonicalProductMapKey(id: string): string {
  return String(id).trim().toLowerCase();
}

export async function fetchProductsByIdsForQtoReview(ids: string[]): Promise<Map<string, QtoReviewProductInfo>> {
  const unique = Array.from(new Set(ids.map((id) => String(id).trim()).filter(Boolean)));
  const byId = new Map<string, QtoReviewProductInfo>();
  if (unique.length === 0) return byId;

  const PRODUCT_LOOKUP_CONCURRENCY = 8;
  for (let i = 0; i < unique.length; i += PRODUCT_LOOKUP_CONCURRENCY) {
    const chunk = unique.slice(i, i + PRODUCT_LOOKUP_CONCURRENCY);
    const responses = await Promise.all(
      chunk.map((id) =>
        fetchData({
          table: "products",
          id,
        })
      )
    );

    for (const response of responses) {
      if (response == null || response.data == null) continue;
      const raw = response.data as { id?: string; name?: string | null; uom?: string | null };
      const id = raw?.id;
      if (typeof id === "string" && id) {
        byId.set(canonicalProductMapKey(id), { name: raw.name ?? "", uom: raw.uom ?? "" });
      }
    }
  }
  return byId;
}

export async function importLineItemsCsv(
  estimateId: string,
  csvContent: string,
  filename = "qto-import.csv"
): Promise<Result<unknown>> {
  const blob = new Blob([csvContent], { type: "text/csv" });
  const formData = new FormData();
  formData.append("file", blob, filename);

  try {
    const data = await writeFormData({
      table: "estimates",
      id: estimateId,
      subRoute: "line_items/import",
      params: { format: "csv" },
      formData,
    });
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
}

/**
 * End-to-end: read user-reviewed rows from the QTO Review sheet, build CSV,
 * and upload it to the given estimate (Add and Update rows; updates use `id`).
 */
